/**
 * Tile Tavern audio architecture.
 *
 * Two buses: `music` (looping ambient bed) and `sfx` (one-shots).
 * Both gated behind a per-device gesture unlock (iOS requires this).
 * Music engine is a tiny vanilla Web Audio scheduler — chord pads,
 * arpeggios, and percussive hits, generated procedurally so we ship
 * zero audio assets.
 *
 * Public API:
 *   audio.unlock()                  — call on first user gesture
 *   audio.setMood(mood)             — switches the looping music bed
 *   audio.sfx.tick() / .bell() ...  — one-shot SFX
 *   audio.prefs.setMusicVol(v)      — 0..1
 *   audio.prefs.setSfxVol(v)        — 0..1
 *   audio.prefs.setMuteAll(b)
 */

const STORAGE_KEY = 'tt:audio';

export type Ambience = 'silent' | 'tavern' | 'cellar' | 'forest' | 'sea' | 'storm';

export type Mood =
  | 'silent'
  | 'gate'
  | 'select'
  | 'home'
  | 'lobby'
  | 'voting'
  | 'final'
  | 'tap-race'
  | 'witch-switch'
  | 'tarot-tap'
  | 'spice-rush'
  | 'trace-race'
  | 'ouija-roulette'
  | 'quick-draw'
  | 'one-of-us'
  | 'coin-conveyer'
  | 'hexagon-heat'
  | 'last-lantern'
  | 'lumber-tumble'
  | 'climb-spire'
  | 'burnstile'
  | 'stick-brawl'
  | 'draft-duel'
  | 'banner-bash'
  | 'trapline'
  | 'pirate-pulley'
  | 'sky-sieve'
  | 'cellar-sweep'
  | 'phantom-pursuit'
  | 'heist-heist'
  | 'stack-smack'
  | 'plinko-tavern'
  | 'rope-walk'
  | 'relay-forge'
  | 'splatter-square'
  | 'cipher-cellar'
  | 'quill-race';

interface Prefs {
  musicVol: number;   // 0..1
  sfxVol: number;     // 0..1
  ambienceVol: number; // 0..1
  muted: boolean;     // master kill
  haptics: boolean;
}

const DEFAULT_PREFS: Prefs = {
  musicVol: 0.45, sfxVol: 0.7, ambienceVol: 0.25, muted: false, haptics: true,
};

function loadPrefs(): Prefs {
  if (typeof localStorage === 'undefined') return { ...DEFAULT_PREFS };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    const p = JSON.parse(raw) as Partial<Prefs>;
    return {
      musicVol: typeof p.musicVol === 'number' ? p.musicVol : DEFAULT_PREFS.musicVol,
      sfxVol: typeof p.sfxVol === 'number' ? p.sfxVol : DEFAULT_PREFS.sfxVol,
      ambienceVol: typeof p.ambienceVol === 'number' ? p.ambienceVol : DEFAULT_PREFS.ambienceVol,
      muted: !!p.muted,
      haptics: p.haptics !== false,
    };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}
function savePrefs(p: Prefs): void {
  if (typeof localStorage === 'undefined') return;
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(p)); } catch { /* ignore */ }
}

let prefs: Prefs = loadPrefs();
let ctx: AudioContext | null = null;
let musicGain: GainNode | null = null;
let sfxGain: GainNode | null = null;
let ambientGain: GainNode | null = null;
let masterGain: GainNode | null = null;
let reverbInput: GainNode | null = null;
let reverbOutput: GainNode | null = null;
let musicReverbSend: GainNode | null = null;
let unlocked = false;
let currentMood: Mood = 'silent';
let currentAmbience: Ambience = 'silent';
let ambienceCleanup: (() => void) | null = null;
let scheduler: number | null = null;
let nextNoteTime = 0;
let phraseStart = 0;
let phraseStep = 0;

/** Subscribers notified when the AudioContext + sfx bus are ready. */
const attachListeners = new Set<(ctx: AudioContext, sfxBus: AudioNode) => void>();

export function onAudioReady(cb: (ctx: AudioContext, sfxBus: AudioNode) => void): () => void {
  attachListeners.add(cb);
  if (ctx && sfxGain) cb(ctx, sfxGain);
  return () => attachListeners.delete(cb);
}

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!ctx) {
    const C = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!C) return null;
    ctx = new C();
    masterGain = ctx.createGain();
    masterGain.gain.value = prefs.muted ? 0 : 1;
    masterGain.connect(ctx.destination);

    musicGain = ctx.createGain();
    musicGain.gain.value = prefs.musicVol;
    musicGain.connect(masterGain);

    sfxGain = ctx.createGain();
    sfxGain.gain.value = prefs.sfxVol;
    sfxGain.connect(masterGain);

    ambientGain = ctx.createGain();
    ambientGain.gain.value = prefs.ambienceVol;
    ambientGain.connect(masterGain);

    // Convolution-reverb send bus. SFX and music tap `reverbInput` via
    // small send gains to colour the dry signal with a procedural room IR.
    const convolver = ctx.createConvolver();
    convolver.buffer = buildReverbIR(ctx, 2.6, 2.4);
    reverbInput = ctx.createGain();
    reverbInput.gain.value = 1;
    reverbOutput = ctx.createGain();
    reverbOutput.gain.value = 0.22;
    reverbInput.connect(convolver);
    convolver.connect(reverbOutput);
    reverbOutput.connect(masterGain);

    // Music sends a fixed amount of its bus into the reverb so pads
    // breathe a hall around them.
    musicReverbSend = ctx.createGain();
    musicReverbSend.gain.value = 0.25;
    musicGain.connect(musicReverbSend);
    musicReverbSend.connect(reverbInput);

    for (const cb of attachListeners) cb(ctx, sfxGain);
  }
  return ctx;
}

// ── Reverb impulse-response generator ───────────────────────────────────
//
// Produces a stereo IR that decays exponentially. `decay` controls the
// curve power (higher = faster fade). Pre-built once on context init —
// generating per-voice is far too expensive.
function buildReverbIR(audioCtx: AudioContext, seconds: number, decay: number): AudioBuffer {
  const sr = audioCtx.sampleRate;
  const length = Math.max(1, Math.floor(sr * seconds));
  const buf = audioCtx.createBuffer(2, length, sr);
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    for (let i = 0; i < length; i++) {
      const t = 1 - i / length;
      // Slight per-channel decorrelation widens the stereo image.
      const jitter = ch === 0 ? 1 : 0.96;
      d[i] = (Math.random() * 2 - 1) * Math.pow(t * jitter, decay);
    }
  }
  return buf;
}

// ── Music ducking ────────────────────────────────────────────────────────
//
// Side-chain the music bus down briefly when a high-impact SFX plays so
// transients punch through instead of fighting the pad. Recovery is slow
// enough to feel musical (~400ms).
function duckMusic(amount = 0.5, holdMs = 220, recoverMs = 420): void {
  if (!musicGain || !ctx) return;
  const t = ctx.currentTime;
  const target = prefs.musicVol * amount;
  const tau = 0.05;
  musicGain.gain.cancelScheduledValues(t);
  musicGain.gain.setTargetAtTime(target, t, tau);
  musicGain.gain.setTargetAtTime(prefs.musicVol, t + holdMs / 1000, recoverMs / 1000 / 3);
}

// ── Ambience generators ─────────────────────────────────────────────────
//
// Each ambience is a SINGLE continuous filtered-noise bed — no scheduled
// clicks, drips, or thunder. Volumes are tuned so even with the slider at
// max they sit far below the music. The whole bus also defaults to 0.25.

/** Build a looping brown-noise source. Returns { node, stop }. */
function brownNoiseLoop(seconds: number): { src: AudioBufferSourceNode; stop: () => void } | null {
  if (!ctx) return null;
  const len = ctx.sampleRate * seconds;
  const buf = ctx.createBuffer(2, len, ctx.sampleRate);
  for (let c = 0; c < 2; c++) {
    const d = buf.getChannelData(c);
    let last = 0;
    for (let i = 0; i < len; i++) {
      const w = Math.random() * 2 - 1;
      last = (last + 0.018 * w) / 1.018;
      d[i] = last * 3.0;
    }
  }
  const src = ctx.createBufferSource();
  src.buffer = buf;
  src.loop = true;
  src.start();
  return { src, stop: () => { try { src.stop(); } catch { /* ignore */ } } };
}

function buildAmbienceTavern(): () => void {
  if (!ctx || !ambientGain) return () => {};
  const node = brownNoiseLoop(4);
  if (!node) return () => {};
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass'; lp.frequency.value = 180; lp.Q.value = 0.4;
  const g = ctx.createGain();
  g.gain.value = 0.12;
  node.src.connect(lp).connect(g).connect(ambientGain);
  return node.stop;
}

function buildAmbienceCellar(): () => void {
  if (!ctx || !ambientGain) return () => {};
  const node = brownNoiseLoop(5);
  if (!node) return () => {};
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass'; lp.frequency.value = 110; lp.Q.value = 0.4;
  const g = ctx.createGain();
  g.gain.value = 0.16;
  node.src.connect(lp).connect(g).connect(ambientGain);
  return node.stop;
}

function buildAmbienceForest(): () => void {
  if (!ctx || !ambientGain) return () => {};
  const audioCtx = ctx;
  const len = audioCtx.sampleRate * 6;
  const buf = audioCtx.createBuffer(2, len, audioCtx.sampleRate);
  for (let c = 0; c < 2; c++) {
    const d = buf.getChannelData(c);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  }
  const wind = audioCtx.createBufferSource();
  wind.buffer = buf; wind.loop = true;
  const bp = audioCtx.createBiquadFilter();
  bp.type = 'bandpass'; bp.frequency.value = 500; bp.Q.value = 0.6;
  const g = audioCtx.createGain();
  g.gain.value = 0.07;
  wind.connect(bp).connect(g).connect(ambientGain);
  // Gentle LFO on volume so the wind breathes instead of sitting flat.
  const lfo = audioCtx.createOscillator();
  const lfoGain = audioCtx.createGain();
  lfo.frequency.value = 0.06;
  lfoGain.gain.value = 0.03;
  lfo.connect(lfoGain); lfoGain.connect(g.gain);
  wind.start(); lfo.start();
  return () => {
    try { wind.stop(); } catch { /* ignore */ }
    try { lfo.stop(); } catch { /* ignore */ }
  };
}

function buildAmbienceSea(): () => void {
  if (!ctx || !ambientGain) return () => {};
  const audioCtx = ctx;
  const len = audioCtx.sampleRate * 8;
  const buf = audioCtx.createBuffer(2, len, audioCtx.sampleRate);
  for (let c = 0; c < 2; c++) {
    const d = buf.getChannelData(c);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  }
  const waves = audioCtx.createBufferSource();
  waves.buffer = buf; waves.loop = true;
  const lp = audioCtx.createBiquadFilter();
  lp.type = 'lowpass'; lp.frequency.value = 600; lp.Q.value = 0.5;
  const g = audioCtx.createGain();
  g.gain.value = 0.13;
  waves.connect(lp).connect(g).connect(ambientGain);
  // Slow filter sweep makes the noise roll like distant surf.
  const lfo = audioCtx.createOscillator();
  const lfoG = audioCtx.createGain();
  lfo.frequency.value = 0.1;
  lfoG.gain.value = 240;
  lfo.connect(lfoG); lfoG.connect(lp.frequency);
  waves.start(); lfo.start();
  return () => {
    try { waves.stop(); } catch { /* ignore */ }
    try { lfo.stop(); } catch { /* ignore */ }
  };
}

function buildAmbienceStorm(): () => void {
  if (!ctx || !ambientGain) return () => {};
  const audioCtx = ctx;
  const len = audioCtx.sampleRate * 6;
  const buf = audioCtx.createBuffer(2, len, audioCtx.sampleRate);
  for (let c = 0; c < 2; c++) {
    const d = buf.getChannelData(c);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  }
  const rain = audioCtx.createBufferSource();
  rain.buffer = buf; rain.loop = true;
  const bp = audioCtx.createBiquadFilter();
  bp.type = 'bandpass'; bp.frequency.value = 2400; bp.Q.value = 0.7;
  const g = audioCtx.createGain();
  g.gain.value = 0.10;
  rain.connect(bp).connect(g).connect(ambientGain);
  rain.start();
  return () => { try { rain.stop(); } catch { /* ignore */ } };
}

const AMBIENCE_BUILDERS: Record<Exclude<Ambience, 'silent'>, () => () => void> = {
  tavern: buildAmbienceTavern,
  cellar: buildAmbienceCellar,
  forest: buildAmbienceForest,
  sea: buildAmbienceSea,
  storm: buildAmbienceStorm,
};

function startAmbience(): void {
  if (currentAmbience === 'silent') return;
  if (ambienceCleanup) return;
  const builder = AMBIENCE_BUILDERS[currentAmbience];
  ambienceCleanup = builder();
}

function stopAmbience(): void {
  if (ambienceCleanup) {
    ambienceCleanup();
    ambienceCleanup = null;
  }
}

function reducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

// ── Procedural music engine ─────────────────────────────────────────────

interface MoodSpec {
  bpm: number;
  /** Chord progression as MIDI note arrays per beat in a 4-bar phrase. */
  chords: number[][];
  /** Arpeggio melody as MIDI notes (one per 8th note) for 4 bars; null = rest. */
  melody?: (number | null)[];
  /** Percussion pattern: 1 = hit, 0 = rest; 16 steps per bar. */
  perc?: number[];
  /** Pad waveform */
  padWave?: OscillatorType;
  /** Color name (CSS hint, unused here but documented) */
  color?: string;
}

const MOODS: Record<Exclude<Mood, 'silent'>, MoodSpec> = {
  gate: {
    bpm: 60,
    chords: [
      [50, 53, 57], // Dm
      [50, 53, 57],
      [48, 52, 55], // C
      [48, 52, 55],
    ],
    padWave: 'sine',
  },
  select: {
    bpm: 70,
    chords: [
      [55, 58, 62], // Gm
      [55, 58, 62],
      [53, 57, 60], // F
      [53, 57, 60],
    ],
    padWave: 'triangle',
  },
  home: {
    bpm: 80,
    chords: [
      [50, 54, 57],
      [52, 55, 59],
      [53, 57, 60],
      [55, 58, 62],
    ],
    padWave: 'triangle',
  },
  lobby: {
    bpm: 86,
    chords: [
      [48, 52, 55],
      [50, 53, 57],
      [52, 55, 59],
      [50, 53, 57],
    ],
    melody: [60, null, 64, null, 67, 64, 60, null, 62, 65, 67, null, 64, 60, null, null,
             60, null, 64, null, 67, 64, 60, null, 62, 65, 64, null, 62, 60, null, null],
    padWave: 'triangle',
  },
  voting: {
    bpm: 96,
    chords: [
      [49, 52, 56],
      [49, 52, 56],
      [51, 54, 58],
      [49, 52, 56],
    ],
    padWave: 'sawtooth',
  },
  final: {
    bpm: 100,
    chords: [
      [48, 52, 55, 60],
      [50, 53, 57, 62],
      [55, 58, 62, 65],
      [48, 52, 55, 60],
    ],
    melody: [72, 71, 69, 67, 64, 67, 72, 76, 79, 76, 72, 71, 69, 67, 65, 64,
             65, 67, 69, 71, 72, 71, 69, 67, 64, 67, 72, 76, 79, 81, 84, null],
    padWave: 'triangle',
  },
  'tap-race': {
    bpm: 130,
    chords: [
      [45, 49, 52],
      [45, 49, 52],
      [50, 54, 57],
      [50, 54, 57],
    ],
    perc: [1, 0, 0, 0, 1, 0, 1, 0, 1, 0, 0, 0, 1, 0, 1, 0],
    padWave: 'sawtooth',
  },
  'witch-switch': {
    bpm: 70,
    chords: [
      [44, 47, 51],
      [45, 48, 52],
      [44, 47, 51],
      [43, 46, 50],
    ],
    padWave: 'sawtooth',
  },
  'tarot-tap': {
    bpm: 110,
    chords: [
      [50, 53, 57],
      [52, 55, 59],
      [55, 58, 62],
      [52, 55, 59],
    ],
    melody: [62, 64, 65, 67, 69, 67, 65, 64, 62, 64, 65, 67, 65, 64, 62, null,
             67, 69, 71, 72, 71, 69, 67, 65, 64, 62, 64, 65, 67, 65, 64, null],
    padWave: 'triangle',
  },
  // Phase 6.1+ — each new game gets its own bed so nothing falls back
  // to the calm 'lobby' loop while you're playing a high-energy round.
  'spice-rush': {
    bpm: 120,
    chords: [
      [48, 52, 55],
      [50, 53, 57],
      [52, 55, 59],
      [50, 53, 57],
    ],
    melody: [60, 64, 67, 64, 60, 64, 67, 71, 67, 64, 60, 64, 67, 64, 60, null,
             62, 65, 69, 65, 62, 65, 69, 72, 69, 65, 62, 65, 69, 65, 62, null],
    padWave: 'triangle',
  },
  'trace-race': {
    bpm: 88,
    chords: [
      [50, 53, 57],
      [50, 53, 57],
      [55, 58, 62],
      [55, 58, 62],
    ],
    melody: [67, null, 69, null, 71, null, 72, null, 71, null, 69, null, 67, null, null, null,
             65, null, 67, null, 69, null, 71, null, 72, 71, 69, 67, 65, null, null, null],
    padWave: 'triangle',
  },
  'ouija-roulette': {
    bpm: 76,
    chords: [
      [43, 46, 50],
      [44, 47, 51],
      [42, 45, 49],
      [43, 46, 50],
    ],
    padWave: 'sawtooth',
  },
  'quick-draw': {
    bpm: 100,
    chords: [
      [50, 53, 57],
      [52, 56, 59],
      [55, 58, 62],
      [50, 53, 57],
    ],
    melody: [62, 64, 65, 67, null, 67, 65, 64, 62, 64, 65, 64, 62, null, null, null,
             64, 65, 67, 69, null, 69, 67, 65, 64, 65, 67, 65, 64, null, null, null],
    padWave: 'triangle',
  },
  'one-of-us': {
    bpm: 92,
    chords: [
      [45, 48, 52],
      [45, 48, 52],
      [44, 47, 51],
      [43, 46, 50],
    ],
    melody: [60, null, null, 63, null, 67, null, 63, 60, null, null, 63, null, 67, 70, null],
    padWave: 'sawtooth',
  },
  'coin-conveyer': {
    bpm: 116,
    chords: [
      [48, 52, 55],
      [48, 52, 55],
      [53, 57, 60],
      [50, 53, 57],
    ],
    perc: [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0],
    melody: [60, 64, 67, 64, 60, 64, 67, 64, 65, 69, 72, 69, 65, 69, 72, null],
    padWave: 'sawtooth',
  },
  'hexagon-heat': {
    bpm: 138,
    chords: [
      [45, 48, 52],
      [45, 48, 52],
      [50, 53, 57],
      [50, 53, 57],
    ],
    perc: [1, 0, 1, 1, 1, 0, 1, 0, 1, 0, 1, 1, 1, 0, 1, 1],
    melody: [60, 60, null, 67, 67, null, 65, 64, 62, 60, null, 67, null, 65, 64, null],
    padWave: 'sawtooth',
  },
  'last-lantern': {
    bpm: 84,
    chords: [
      [44, 47, 51],
      [44, 47, 51],
      [43, 46, 50],
      [42, 45, 49],
    ],
    melody: [56, null, null, null, 60, null, 63, null, 60, null, null, 56, null, null, null, null],
    padWave: 'sawtooth',
  },
  'lumber-tumble': {
    bpm: 124,
    chords: [
      [48, 52, 55],
      [50, 53, 57],
      [48, 52, 55],
      [47, 50, 54],
    ],
    perc: [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0],
    padWave: 'sawtooth',
  },
  'climb-spire': {
    bpm: 108,
    chords: [
      [45, 49, 52],
      [47, 50, 54],
      [50, 53, 57],
      [52, 56, 59],
    ],
    melody: [57, 60, 64, 67, 72, 67, 64, 60, 57, 60, 64, 67, 72, 76, 72, null,
             64, 67, 71, 74, 79, 74, 71, 67, 64, 67, 71, 74, 79, 83, 79, null],
    padWave: 'triangle',
  },
  // Phase 6.4 — final 3 game beds.
  'burnstile': {
    // Tense, mechanical metronome — tempo cranks the urgency to 11.
    bpm: 142,
    chords: [
      [45, 48, 52],
      [45, 48, 52],
      [44, 47, 51],
      [43, 46, 50],
    ],
    perc: [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0],
    melody: [69, 72, 69, 72, 67, 72, 69, 72, 67, 70, 67, 70, 65, 70, 67, 70],
    padWave: 'sawtooth',
  },
  'stick-brawl': {
    // Heavy taiko-ish brawl bed — driving downbeat, room for chaos on top.
    bpm: 132,
    chords: [
      [45, 48, 52],
      [50, 53, 57],
      [48, 52, 55],
      [47, 50, 54],
    ],
    perc: [1, 0, 1, 1, 1, 0, 1, 1, 1, 0, 1, 1, 1, 0, 1, 1],
    padWave: 'sawtooth',
  },
  'draft-duel': {
    // Mystical fanfare for drafting + battle.
    bpm: 96,
    chords: [
      [50, 53, 57],
      [52, 55, 59],
      [55, 58, 62],
      [50, 53, 57],
    ],
    melody: [62, 65, 69, 72, 71, 67, 65, 62, 64, 67, 71, 74, 72, 69, 67, null,
             62, 65, 69, 72, 74, 72, 69, 65, 64, 67, 71, 74, 72, 69, 65, null],
    padWave: 'triangle',
  },
  'banner-bash': {
    // Capture-the-flag urgency — driving 4-on-the-floor with sprint feel.
    bpm: 124,
    chords: [
      [48, 52, 55],
      [50, 53, 57],
      [55, 58, 62],
      [50, 53, 57],
    ],
    perc: [1, 0, 0, 1, 0, 1, 0, 0, 1, 0, 0, 1, 0, 1, 0, 1],
    padWave: 'sawtooth',
  },
  'trapline': {
    // Tense maze stalker — minor chords, sparse pad.
    bpm: 84,
    chords: [
      [43, 46, 50],
      [44, 47, 51],
      [42, 45, 49],
      [43, 46, 50],
    ],
    padWave: 'sawtooth',
  },
  'pirate-pulley': {
    // Sea shanty tug-of-war — bouncy 6/8 feel via 4/4 + accents.
    bpm: 110,
    chords: [
      [48, 52, 55],
      [50, 53, 57],
      [52, 55, 59],
      [48, 52, 55],
    ],
    perc: [1, 0, 1, 0, 1, 0, 1, 1, 1, 0, 1, 0, 1, 0, 1, 1],
    padWave: 'triangle',
  },
  'sky-sieve': {
    // Tense falling-blocks bed — minor with dotted urgency, accelerates feel.
    bpm: 128,
    chords: [
      [45, 48, 52],
      [47, 50, 54],
      [48, 52, 55],
      [45, 48, 52],
    ],
    perc: [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 1],
    padWave: 'sawtooth',
  },
  'cellar-sweep': {
    // Co-op shootout — driving low pad with mid arpeggio, action-movie feel.
    bpm: 130,
    chords: [
      [40, 43, 47],
      [43, 47, 50],
      [38, 42, 45],
      [40, 43, 47],
    ],
    melody: [60, null, 63, null, 65, null, 63, 60, 58, null, 60, null, 63, null, null, null,
             60, null, 63, null, 67, 65, 63, 60, 58, null, 60, null, 63, null, 65, null],
    perc: [1, 0, 1, 0, 1, 1, 0, 0, 1, 0, 1, 0, 1, 0, 1, 0],
    padWave: 'sawtooth',
  },
  'phantom-pursuit': {
    // Ghostly pursuit — slow eerie sustain.
    bpm: 76,
    chords: [
      [42, 45, 49],
      [44, 47, 51],
      [40, 43, 47],
      [42, 45, 49],
    ],
    padWave: 'sine',
  },
  'heist-heist': {
    // Stealth caper — jazzy walking-bass minor, cat-and-mouse vibe.
    bpm: 92,
    chords: [
      [44, 47, 51],
      [46, 49, 53],
      [47, 50, 54],
      [44, 47, 51],
    ],
    perc: [1, 0, 0, 0, 1, 0, 1, 0, 1, 0, 0, 0, 1, 0, 1, 0],
    padWave: 'triangle',
  },
  'stack-smack': {
    // 2v2 reverse-Tetris — pulsing strategic bed.
    bpm: 116,
    chords: [
      [48, 51, 55],
      [50, 53, 57],
      [53, 56, 60],
      [50, 53, 57],
    ],
    melody: [60, null, 63, null, 65, 63, 60, null, 62, 65, 67, null, 63, 60, null, null,
             60, null, 63, null, 65, 63, 60, null, 62, 65, 63, null, 62, 60, null, null],
    padWave: 'triangle',
  },
  'plinko-tavern': {
    // Bouncy ball-drop carnival vibe — major triads, twinkling.
    bpm: 100,
    chords: [
      [48, 52, 55],
      [50, 53, 57],
      [55, 58, 62],
      [48, 52, 55],
    ],
    melody: [72, 76, 79, 76, 72, 76, 79, 76, 74, 77, 81, 77, 74, 77, 81, null,
             72, 76, 79, 84, 79, 76, 72, null, 74, 77, 81, 84, 81, 77, 74, null],
    padWave: 'triangle',
  },
  'rope-walk': {
    // High-altitude tightrope — sparse, breathy, tense balance.
    bpm: 88,
    chords: [
      [50, 53, 57],
      [50, 53, 57],
      [52, 55, 59],
      [50, 53, 57],
    ],
    padWave: 'sine',
  },
  'relay-forge': {
    // Forge anvil-rhythm bed — heavy downbeat for hammer cues.
    bpm: 120,
    chords: [
      [40, 43, 47],
      [43, 47, 50],
      [40, 43, 47],
      [38, 41, 45],
    ],
    perc: [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0],
    padWave: 'sawtooth',
  },
  'splatter-square': {
    // Joyful chase — bright major arpeggio.
    bpm: 118,
    chords: [
      [50, 54, 57],
      [52, 55, 59],
      [55, 58, 62],
      [50, 54, 57],
    ],
    melody: [62, 64, 65, 67, 69, 67, 65, 64, 62, 64, 65, 67, 69, 71, 72, null,
             65, 67, 69, 71, 72, 71, 69, 67, 65, 64, 65, 67, 69, 67, 65, null],
    padWave: 'triangle',
  },
  'cipher-cellar': {
    // Letter-puzzle dim cellar — soft contemplative pad.
    bpm: 78,
    chords: [
      [48, 52, 55],
      [50, 53, 57],
      [52, 55, 59],
      [50, 53, 57],
    ],
    padWave: 'sine',
  },
  'quill-race': {
    // Quill-on-parchment focus — scribbling staccato + soft pad.
    bpm: 104,
    chords: [
      [52, 55, 59],
      [53, 57, 60],
      [55, 58, 62],
      [52, 55, 59],
    ],
    perc: [1, 1, 0, 1, 1, 0, 1, 1, 1, 1, 0, 1, 1, 0, 1, 0],
    padWave: 'triangle',
  },
};

function midiToHz(m: number): number { return 440 * Math.pow(2, (m - 69) / 12); }

/**
 * Pad voice with 3 detuned oscillators per note (±7 cents), a lowpass
 * filter that opens over the attack, and a slight L/R pan per note.
 * Result: a thick, warm choir-ish pad instead of a single buzzy osc.
 */
function playPad(when: number, freqs: number[], durSec: number, wave: OscillatorType, vol = 0.06): void {
  if (!ctx || !musicGain) return;
  const perNote = vol / Math.max(1, freqs.length);
  for (let n = 0; n < freqs.length; n++) {
    const f = freqs[n]!;
    const voiceGain = ctx.createGain();
    voiceGain.gain.setValueAtTime(0, when);
    voiceGain.gain.linearRampToValueAtTime(perNote, when + 0.6);
    voiceGain.gain.linearRampToValueAtTime(0.0001, when + durSec);

    // Per-note lowpass that opens during attack — gives the pad
    // motion + softens digital harmonics. Cutoff scales with pitch so
    // bass notes stay warm and high notes don't get smothered.
    const filt = ctx.createBiquadFilter();
    filt.type = 'lowpass';
    filt.Q.value = 0.5;
    filt.frequency.setValueAtTime(Math.min(f * 2.4, 900), when);
    filt.frequency.exponentialRampToValueAtTime(Math.min(f * 8, 6000), when + 1.2);

    // Stereo pan: chord notes spread across the field.
    const panNode = ctx.createStereoPanner();
    panNode.pan.value = freqs.length > 1 ? (n / (freqs.length - 1) - 0.5) * 0.6 : 0;

    // 3-osc detune stack. Two slightly-flat + one slightly-sharp create
    // beating at ~1Hz which reads as thickness.
    const detunes = [-7, 0, 7];
    for (const cents of detunes) {
      const o = ctx.createOscillator();
      o.type = wave;
      o.frequency.value = f;
      o.detune.value = cents;
      o.connect(filt);
      o.start(when);
      o.stop(when + durSec + 0.1);
    }
    filt.connect(voiceGain);
    voiceGain.connect(panNode);
    panNode.connect(musicGain);
  }
}

/**
 * Sub-bass voice: a single sine 1-2 octaves below the chord root with a
 * gentle attack/decay. Gives the pad weight without muddying the mix.
 */
function playBass(when: number, rootFreq: number, durSec: number, vol = 0.07): void {
  if (!ctx || !musicGain) return;
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.type = 'sine';
  o.frequency.value = rootFreq * 0.5;
  g.gain.setValueAtTime(0, when);
  g.gain.linearRampToValueAtTime(vol, when + 0.08);
  g.gain.setValueAtTime(vol, when + durSec * 0.6);
  g.gain.exponentialRampToValueAtTime(0.0001, when + durSec);
  o.connect(g);
  g.connect(musicGain);
  o.start(when);
  o.stop(when + durSec + 0.05);
}

/**
 * Melody pluck. Two layered oscillators (triangle + sine octave-up at low
 * level) plus a snappy lowpass envelope give it a soft-mallet character
 * instead of a pure beep.
 */
function playPluck(when: number, freq: number, vol = 0.08): void {
  if (!ctx || !musicGain) return;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0, when);
  g.gain.linearRampToValueAtTime(vol, when + 0.005);
  g.gain.exponentialRampToValueAtTime(0.0001, when + 0.42);

  const filt = ctx.createBiquadFilter();
  filt.type = 'lowpass';
  filt.Q.value = 0.7;
  filt.frequency.setValueAtTime(freq * 6, when);
  filt.frequency.exponentialRampToValueAtTime(freq * 2, when + 0.4);

  const o1 = ctx.createOscillator();
  o1.type = 'triangle';
  o1.frequency.value = freq;
  o1.connect(filt);
  const o2 = ctx.createOscillator();
  o2.type = 'sine';
  o2.frequency.value = freq * 2; // octave-up shimmer
  const o2g = ctx.createGain();
  o2g.gain.value = 0.35;
  o2.connect(o2g);
  o2g.connect(filt);

  filt.connect(g);
  g.connect(musicGain);
  o1.start(when); o1.stop(when + 0.5);
  o2.start(when); o2.stop(when + 0.5);
}

/**
 * Kick drum: pitch-decaying sine 80→40 Hz over 80ms with a quick body
 * envelope. Replaces the prior white-noise-only perc, which sounded
 * exactly like 8-bit static.
 */
function playKick(when: number, vol = 0.18): void {
  if (!ctx || !musicGain) return;
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.type = 'sine';
  o.frequency.setValueAtTime(120, when);
  o.frequency.exponentialRampToValueAtTime(38, when + 0.08);
  g.gain.setValueAtTime(0, when);
  g.gain.linearRampToValueAtTime(vol, when + 0.003);
  g.gain.exponentialRampToValueAtTime(0.0001, when + 0.18);
  o.connect(g);
  g.connect(musicGain);
  o.start(when);
  o.stop(when + 0.22);

  // Tiny click on the very front gives transient definition.
  const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.015), ctx.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / d.length, 4);
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const cg = ctx.createGain();
  cg.gain.value = vol * 0.25;
  src.connect(cg);
  cg.connect(musicGain);
  src.start(when);
}

/**
 * Hi-hat: high-pass filtered short noise burst. Gives kick patterns a
 * sense of motion without taking over the mix.
 */
function playHat(when: number, vol = 0.04): void {
  if (!ctx || !musicGain) return;
  const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.04), ctx.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / d.length, 3);
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const hp = ctx.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.value = 7000;
  const g = ctx.createGain();
  g.gain.value = vol;
  src.connect(hp).connect(g).connect(musicGain);
  src.start(when);
}

function tick(): void {
  if (currentMood === 'silent' || !ctx) {
    scheduler = null;
    return;
  }
  if (reducedMotion()) {
    scheduler = null;
    return;
  }
  const spec = MOODS[currentMood as Exclude<Mood, 'silent'>];
  const beatSec = 60 / spec.bpm;
  const eighthSec = beatSec / 2;
  const stepSec = beatSec / 4; // 16th
  const phraseSec = beatSec * 16; // 4 bars
  const lookAhead = 0.2;

  while (nextNoteTime < ctx.currentTime + lookAhead) {
    const eighth = phraseStep % 32;

    if (eighth % 8 === 0) {
      const chordIndex = Math.floor(eighth / 8) % spec.chords.length;
      const chord = spec.chords[chordIndex]!;
      const notes = chord.map(midiToHz);
      playPad(nextNoteTime, notes, beatSec * 4, spec.padWave ?? 'sine', 0.045);
      // Sub-bass on the chord root, one octave below already-low root.
      playBass(nextNoteTime, midiToHz(chord[0]!), beatSec * 4, 0.075);
    }
    if (spec.melody) {
      const m = spec.melody[eighth];
      if (m !== null && m !== undefined) {
        playPluck(nextNoteTime, midiToHz(m), 0.06);
      }
    }
    if (spec.perc) {
      const stepIndex = (phraseStep * 2) % 16;
      const hit = spec.perc[stepIndex];
      // Beats 1 and 3 of each bar get a kick; off-beats get a hat. This
      // turns the prior random-noise perc track into a real groove.
      if (hit) {
        if (stepIndex % 4 === 0) playKick(nextNoteTime, 0.16);
        else playHat(nextNoteTime, 0.035);
      }
    }

    nextNoteTime += eighthSec;
    phraseStep++;
    if (phraseStep >= 32) {
      phraseStep = 0;
      phraseStart += phraseSec;
    }
  }
  scheduler = window.setTimeout(tick, 60);
  void stepSec;
}

function startMusic(): void {
  if (!ctx) return;
  if (currentMood === 'silent') return;
  if (scheduler !== null) return;
  nextNoteTime = ctx.currentTime + 0.05;
  phraseStart = nextNoteTime;
  phraseStep = 0;
  tick();
}

function stopMusic(): void {
  if (scheduler !== null) {
    window.clearTimeout(scheduler);
    scheduler = null;
  }
}

// ── SFX library ─────────────────────────────────────────────────────────

// Per-voice reverb send. 0.18 = subtle room presence on every SFX without
// drowning transients. Loud impacts (fanfare, wallCrash) override via the
// optional `sendAmt` parameter.
const DEFAULT_SEND = 0.18;

function tone(
  freq: number, dur: number, vol = 0.12,
  type: OscillatorType = 'sine', when?: number, sendAmt = DEFAULT_SEND,
): void {
  if (prefs.muted || !sfxGain || !ctx) return;
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  // Gentle lowpass smooths digital harshness on saws/squares without
  // changing the perceived pitch — biggest single contributor to the
  // "less 8-bit" character.
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = Math.min(12000, freq * 8 + 1200);
  lp.Q.value = 0.4;
  o.type = type;
  o.frequency.value = freq;
  const t = when ?? ctx.currentTime;
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(vol, t + 0.005);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  o.connect(lp);
  lp.connect(g);
  g.connect(sfxGain);
  if (sendAmt > 0 && reverbInput) {
    const send = ctx.createGain();
    send.gain.value = sendAmt;
    g.connect(send);
    send.connect(reverbInput);
  }
  o.start(t);
  o.stop(t + dur + 0.05);
}

function noise(dur: number, vol = 0.05, when?: number, sendAmt = DEFAULT_SEND): void {
  if (prefs.muted || !sfxGain || !ctx) return;
  const buf = ctx.createBuffer(1, Math.max(1, Math.floor(ctx.sampleRate * dur)), ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / data.length, 2);
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const g = ctx.createGain();
  g.gain.value = vol;
  src.connect(g);
  g.connect(sfxGain);
  if (sendAmt > 0 && reverbInput) {
    const send = ctx.createGain();
    send.gain.value = sendAmt;
    g.connect(send);
    send.connect(reverbInput);
  }
  src.start(when ?? ctx.currentTime);
}

let lastTickPitch = 880;
function tapTick(): void {
  // Slight pitch variation per consecutive tap.
  lastTickPitch = 800 + ((lastTickPitch + 13) % 220);
  tone(lastTickPitch, 0.05, 0.07, 'square');
  vibrate(8);
}

function vibrate(pattern: number | number[]): void {
  if (!prefs.haptics || prefs.muted) return;
  if (typeof navigator === 'undefined') return;
  const n = navigator as Navigator & { vibrate?: (p: number | number[]) => boolean };
  try { n.vibrate?.(pattern); } catch { /* ignore */ }
}

export const audio = {
  unlock(): void {
    const c = getCtx();
    if (!c) return;
    if (c.state === 'suspended') void c.resume();
    unlocked = true;
    if (currentMood !== 'silent') startMusic();
    if (currentAmbience !== 'silent') startAmbience();
  },
  isUnlocked(): boolean { return unlocked; },
  setMood(m: Mood): void {
    if (m === currentMood) return;
    currentMood = m;
    stopMusic();
    if (unlocked && m !== 'silent') startMusic();
  },
  getMood(): Mood { return currentMood; },
  setAmbience(a: Ambience): void {
    if (a === currentAmbience) return;
    currentAmbience = a;
    stopAmbience();
    if (unlocked && a !== 'silent') startAmbience();
  },
  getAmbience(): Ambience { return currentAmbience; },
  sfx: {
    tap: tapTick,
    bell(): void {
      duckMusic(0.55);
      tone(1047, 0.12, 0.16, 'triangle', undefined, 0.4);
      window.setTimeout(() => tone(1568, 0.07, 0.12, 'triangle', undefined, 0.4), 60);
      vibrate(15);
    },
    success(): void {
      duckMusic(0.55);
      const t = ctx?.currentTime ?? 0;
      tone(523, 0.08, 0.12, 'triangle', t, 0.3);
      tone(659, 0.08, 0.12, 'triangle', t + 0.08, 0.3);
      tone(784, 0.18, 0.16, 'triangle', t + 0.16, 0.35);
      vibrate([12, 30, 12]);
    },
    fail(): void {
      duckMusic(0.5);
      tone(196, 0.22, 0.14, 'sawtooth', undefined, 0.35);
      vibrate(80);
    },
    cheer(): void {
      duckMusic(0.45, 380, 600);
      const t = ctx?.currentTime ?? 0;
      tone(523, 0.08, 0.14, 'triangle', t, 0.4);
      tone(659, 0.08, 0.14, 'triangle', t + 0.09, 0.4);
      tone(784, 0.08, 0.14, 'triangle', t + 0.18, 0.4);
      tone(1047, 0.22, 0.18, 'triangle', t + 0.27, 0.5);
      vibrate([20, 40, 60]);
    },
    countdownTick(): void {
      tone(660, 0.08, 0.16, 'square');
      vibrate(6);
    },
    countdownGo(): void {
      duckMusic(0.4, 400, 700);
      const t = ctx?.currentTime ?? 0;
      tone(523, 0.32, 0.18, 'triangle', t, 0.45);
      tone(784, 0.32, 0.18, 'triangle', t, 0.45);
      tone(1047, 0.32, 0.18, 'triangle', t, 0.45);
      vibrate([20, 30, 40]);
    },
    buttonPress(): void {
      tone(220, 0.04, 0.05, 'sine');
    },
    buttonHover(): void {
      tone(440, 0.02, 0.02, 'sine');
    },
    toast(): void {
      tone(1175, 0.08, 0.08, 'triangle');
    },
    tileHover(): void {
      tone(880, 0.02, 0.04, 'sine');
    },
    cardFlip(): void {
      noise(0.08, 0.06);
      tone(380, 0.07, 0.06, 'triangle');
    },
    cardSnap(): void {
      tone(1568, 0.06, 0.12, 'square');
      tone(880, 0.04, 0.08, 'square');
      vibrate(20);
    },
    cardThunk(): void {
      tone(165, 0.12, 0.1, 'sawtooth');
      vibrate(10);
    },
    candleSnuff(): void {
      noise(0.16, 0.08);
    },
    candleGust(): void {
      noise(0.32, 0.12);
    },
    glassTinkle(): void {
      tone(2200, 0.06, 0.1, 'triangle');
      window.setTimeout(() => tone(1760, 0.04, 0.08, 'triangle'), 40);
    },
    siren(): void {
      const t = ctx?.currentTime ?? 0;
      tone(440, 0.5, 0.14, 'sawtooth', t);
      tone(660, 0.5, 0.14, 'sawtooth', t + 0.05);
    },
    fanfare(): void {
      duckMusic(0.4, 500, 900);
      const t = ctx?.currentTime ?? 0;
      tone(523, 0.18, 0.16, 'triangle', t, 0.5);
      tone(659, 0.18, 0.16, 'triangle', t + 0.08, 0.5);
      tone(784, 0.32, 0.18, 'triangle', t + 0.16, 0.55);
      tone(1047, 0.4, 0.2, 'triangle', t + 0.24, 0.6);
    },
    connectionLost(): void {
      tone(110, 0.4, 0.16, 'sine');
    },
    reactClap(): void { tapTick(); window.setTimeout(tapTick, 60); window.setTimeout(tapTick, 110); },
    reactFire(): void { noise(0.18, 0.1); },
    reactSpooky(): void { tone(220, 0.32, 0.1, 'sawtooth'); },
    // ── Per-game spec'd sample names (procedurally generated) ──
    // Cipher Cellar (cipher-cellar.md §15)
    letterReveal(): void {
      const t = ctx?.currentTime ?? 0;
      tone(880, 0.08, 0.12, 'triangle', t);
      tone(1320, 0.16, 0.14, 'triangle', t + 0.05);
      vibrate(8);
    },
    guessCorrect(): void {
      const t = ctx?.currentTime ?? 0;
      tone(523, 0.06, 0.1, 'triangle', t);
      tone(659, 0.06, 0.1, 'triangle', t + 0.05);
      tone(880, 0.12, 0.14, 'triangle', t + 0.1);
    },
    guessWrong(): void {
      tone(140, 0.14, 0.1, 'sine');
      vibrate(20);
    },
    // Quill Race (quill-race.md §15)
    penScratch(pitch?: number): void {
      const p = pitch ?? (0.85 + Math.random() * 0.3);
      noise(0.04, 0.03);
      tone(160 * p, 0.025, 0.04, 'sawtooth');
    },
    inkBlot(): void {
      noise(0.08, 0.07);
      tone(110, 0.16, 0.12, 'sine');
      vibrate(30);
    },
    // Splatter Square (splatter-square.md §15)
    paintSplat(pitch?: number): void {
      const p = pitch ?? (0.85 + Math.random() * 0.3);
      noise(0.06, 0.07);
      tone(220 * p, 0.06, 0.08, 'sine');
    },
    sprintWhoosh(): void {
      noise(0.16, 0.1);
      tone(440, 0.1, 0.06, 'triangle');
    },
    woodCreak(): void {
      const t = ctx?.currentTime ?? 0;
      tone(180, 0.18, 0.08, 'sawtooth', t);
      tone(140, 0.22, 0.08, 'sawtooth', t + 0.08);
    },
    // Sky Sieve (sky-sieve.md §15)
    blockImpact(): void {
      noise(0.08, 0.18);
      tone(85, 0.18, 0.16, 'sawtooth');
      vibrate(40);
    },
    blockWhoosh(): void {
      noise(0.04, 0.06);
    },
    closeCall(): void {
      const t = ctx?.currentTime ?? 0;
      tone(1760, 0.04, 0.06, 'triangle', t);
      tone(2200, 0.04, 0.06, 'triangle', t + 0.02);
      tone(2640, 0.06, 0.08, 'triangle', t + 0.04);
    },
    // Trapline (trapline.md §15)
    /** §15 trap-snap: sharp snap, low + high pitch layered. §15.1 pitch variance per cell. */
    trapSnap(pitch?: number): void {
      const p = pitch ?? (0.9 + Math.random() * 0.25);
      const t = ctx?.currentTime ?? 0;
      // High-pitch metal snap
      tone(1320 * p, 0.04, 0.16, 'square', t);
      // Low-pitch jaw thud
      tone(220 * p, 0.08, 0.14, 'sawtooth', t + 0.01);
      noise(0.06, 0.14);
      vibrate(45);
    },
    /** §15 step-click: subtle click on each grid move (rate-limited externally to 5/sec). */
    stepClick(): void {
      tone(380, 0.025, 0.06, 'square');
    },
    /** §15 plant-thud: wooden thud when planting a trap on a cell. */
    plantThud(): void {
      const t = ctx?.currentTime ?? 0;
      tone(180, 0.06, 0.14, 'sawtooth', t);
      tone(110, 0.05, 0.10, 'sine', t + 0.02);
      noise(0.04, 0.04);
      vibrate(14);
    },
    // Plinko Tavern (plinko-tavern.md §15)
    pegClack(pitch?: number): void {
      const p = pitch ?? (0.85 + Math.random() * 0.3);
      tone(1200 * p, 0.025, 0.08, 'square');
    },
    coinDrop(): void {
      const t = ctx?.currentTime ?? 0;
      tone(880, 0.06, 0.1, 'triangle', t);
      tone(660, 0.06, 0.1, 'triangle', t + 0.05);
    },
    magnetPulse(): void {
      const t = ctx?.currentTime ?? 0;
      tone(440, 0.08, 0.1, 'sine', t);
      tone(660, 0.16, 0.12, 'sine', t + 0.05);
    },
    burstPop(): void {
      noise(0.12, 0.16);
      tone(220, 0.08, 0.12, 'sawtooth');
    },
    // Pirate Pulley (pirate-pulley.md §15)
    ropePull(): void {
      tone(280, 0.04, 0.08, 'sawtooth');
    },
    waveRush(): void {
      noise(0.4, 0.18);
    },
    splash(): void {
      noise(0.18, 0.12);
      tone(420, 0.08, 0.1, 'sine');
    },
    // Cellar Sweep (cellar-sweep.md §15)
    bulletPop(): void {
      tone(1500, 0.025, 0.06, 'square');
    },
    monsterSnarl(pitch?: number): void {
      const p = pitch ?? (0.85 + Math.random() * 0.3);
      tone(180 * p, 0.06, 0.1, 'sawtooth');
      noise(0.04, 0.06);
    },
    bossRoar(): void {
      duckMusic(0.3, 600, 900);
      const t = ctx?.currentTime ?? 0;
      tone(80, 0.6, 0.18, 'sawtooth', t, 0.6);
      tone(60, 0.5, 0.16, 'sawtooth', t + 0.1, 0.6);
      noise(0.5, 0.14, undefined, 0.55);
      vibrate(80);
    },
    downedThud(): void {
      tone(140, 0.18, 0.12, 'sawtooth');
      noise(0.2, 0.1);
      vibrate(60);
    },
    reviveChime(): void {
      const t = ctx?.currentTime ?? 0;
      tone(523, 0.06, 0.12, 'triangle', t);
      tone(784, 0.06, 0.12, 'triangle', t + 0.06);
      tone(1047, 0.16, 0.14, 'triangle', t + 0.12);
    },
    // Phantom Pursuit (phantom-pursuit.md §15)
    phantomWhoosh(): void {
      noise(0.18, 0.14);
      tone(660, 0.12, 0.1, 'sine');
    },
    pingMiss(): void {
      tone(420, 0.06, 0.08, 'triangle');
      tone(220, 0.06, 0.06, 'triangle');
    },
    phantomCaught(): void {
      const t = ctx?.currentTime ?? 0;
      tone(120, 0.16, 0.16, 'sawtooth', t);
      tone(80, 0.18, 0.14, 'sawtooth', t + 0.06);
      vibrate(60);
    },
    // Heist Heist (heist-heist.md §15)
    vaultClick(): void {
      tone(1200, 0.035, 0.1, 'square');
    },
    alarmBlip(): void {
      tone(900, 0.08, 0.12, 'sawtooth');
    },
    laserScan(): void {
      const t = ctx?.currentTime ?? 0;
      tone(2200, 0.04, 0.06, 'triangle', t);
      tone(1760, 0.04, 0.06, 'triangle', t + 0.03);
    },
    /** §15 footstep — very subtle scuff. Throttle to 5/sec/thief externally. */
    footstep(pitch?: number): void {
      const p = pitch ?? (0.9 + Math.random() * 0.2);
      noise(0.02, 0.02);
      tone(140 * p, 0.02, 0.025, 'sine');
    },
    /** §15 pulse-zap — descending tone (guard pulse). */
    pulseZap(): void {
      const t = ctx?.currentTime ?? 0;
      tone(880, 0.04, 0.12, 'sawtooth', t);
      tone(440, 0.08, 0.14, 'sawtooth', t + 0.04);
      tone(220, 0.10, 0.10, 'sine', t + 0.10);
      vibrate(30);
    },
    /** §15 gold-grab — ka-ching. §15 pitch-shifted up for cumulative grabs. */
    goldGrab(grabIdx?: number): void {
      const idx = Math.max(0, Math.min(2, grabIdx ?? 0));
      const p = 1 + idx * 0.12; // 1.0, 1.12, 1.24
      const t = ctx?.currentTime ?? 0;
      tone(880 * p, 0.06, 0.14, 'triangle', t);
      tone(1320 * p, 0.08, 0.14, 'triangle', t + 0.05);
      tone(1760 * p, 0.16, 0.16, 'triangle', t + 0.12);
      vibrate(14);
    },
    /** §15 thief-caught — gasp + lock. */
    thiefCaught(): void {
      const t = ctx?.currentTime ?? 0;
      // Gasp: high quick rise
      tone(1320, 0.05, 0.10, 'sine', t);
      // Lock thunk
      tone(180, 0.10, 0.14, 'sawtooth', t + 0.05);
      tone(90, 0.12, 0.10, 'sine', t + 0.08);
      noise(0.10, 0.08);
      vibrate(50);
    },
    // Banner Bash (banner-bash.md §15)
    hammerThud(): void {
      tone(160, 0.05, 0.1, 'sawtooth');
      vibrate(20);
    },
    claimPop(): void {
      const t = ctx?.currentTime ?? 0;
      tone(880, 0.05, 0.1, 'triangle', t);
      tone(1320, 0.05, 0.1, 'triangle', t + 0.04);
    },
    /** §15 banner-pickup: light chime — uplifting two-tone pluck. */
    bannerPickup(): void {
      const t = ctx?.currentTime ?? 0;
      tone(988, 0.06, 0.12, 'triangle', t);
      tone(1480, 0.10, 0.14, 'triangle', t + 0.05);
      vibrate(10);
    },
    /** §15 banner-drop: low thud (also plays on tag for carrier). */
    bannerDrop(): void {
      const t = ctx?.currentTime ?? 0;
      tone(180, 0.10, 0.14, 'sawtooth', t);
      tone(110, 0.14, 0.10, 'sine', t + 0.04);
      noise(0.06, 0.06);
      vibrate(20);
    },
    /** §15 sprint depleted: low-pitched tap-pop variant. */
    sprintDepleted(): void {
      tone(220, 0.06, 0.12, 'square');
      tone(140, 0.04, 0.08, 'sawtooth');
      vibrate(18);
    },
    // Stack Smack (stack-smack.md §15)
    /**
     * §15.1 block-thud (deep impact) + §15.3 — pitch lowers as stack
     * height grows. `heightFrac` is the stack's normalized fill 0..1,
     * where 1 = full column. Lower frac = brighter, higher = bassier.
     */
    blockSlap(heightFrac?: number): void {
      const f = Math.max(0, Math.min(1, heightFrac ?? 0));
      // Higher stack -> lower pitch.
      const baseHz = 260 - f * 140; // 260 → 120 Hz
      tone(baseHz, 0.05, 0.12, 'sawtooth');
      tone(baseHz * 0.5, 0.08, 0.08, 'sine');
      noise(0.05, 0.06);
      vibrate(12 + Math.round(f * 18));
    },
    /** §15.1 wall-crash — louder bass + dust, "cathartic" topple sound. */
    wallCrash(): void {
      duckMusic(0.35, 500, 900);
      const t = ctx?.currentTime ?? 0;
      tone(140, 0.32, 0.22, 'sawtooth', t, 0.6);
      tone(85, 0.4, 0.22, 'sawtooth', t + 0.04, 0.6);
      tone(60, 0.5, 0.18, 'sine', t + 0.1, 0.55);
      noise(0.4, 0.18, undefined, 0.5);
      vibrate([40, 60, 80]);
    },
    /** Backwards-compat alias retained for any earlier callers. */
    stackTumble(): void {
      audio.sfx.wallCrash();
    },
    // Rope Walk (rope-walk.md §15)
    ropeCreak(): void {
      tone(190, 0.16, 0.1, 'sawtooth');
    },
    balanceTick(pitch?: number): void {
      const p = pitch ?? 1;
      tone(660 * p, 0.025, 0.06, 'square');
    },
    /** Subtle creak step — emitted ~once/1.5s per spec §15.1. */
    tightropeStep(): void {
      const p = 0.9 + Math.random() * 0.2;
      tone(220 * p, 0.04, 0.05, 'triangle');
      noise(0.02, 0.02);
    },
    /** Near-fall warning when |poleTilt| > 0.85 × FALL_TILT. */
    tapPop(): void {
      const t = ctx?.currentTime ?? 0;
      tone(1760, 0.04, 0.12, 'square', t);
      tone(2200, 0.03, 0.1, 'square', t + 0.025);
      vibrate(12);
    },
    /** Short uplifting fanfare on rope finish. */
    fanfareShort(): void {
      duckMusic(0.45, 360, 600);
      const t = ctx?.currentTime ?? 0;
      tone(659, 0.08, 0.14, 'triangle', t, 0.4);
      tone(880, 0.08, 0.16, 'triangle', t + 0.06, 0.45);
      tone(1318, 0.16, 0.2, 'triangle', t + 0.14, 0.5);
      vibrate([10, 24, 10]);
    },
    /** Low impact thud when walker falls. */
    fallThud(): void {
      duckMusic(0.5);
      const t = ctx?.currentTime ?? 0;
      tone(110, 0.18, 0.18, 'sawtooth', t, 0.5);
      tone(70, 0.22, 0.16, 'sawtooth', t + 0.04, 0.5);
      noise(0.18, 0.14, undefined, 0.4);
      vibrate(70);
    },
    /**
     * Emit a single tick of the wobble-drone — call repeatedly while a rope
     * is active. Volume scales with the tier's wobbleHz so rope 3 sounds
     * tenser than rope 1. Throttle externally (1 call ~ every 250ms).
     */
    wobbleDrone(intensity?: number): void {
      const i = Math.max(0, Math.min(1, intensity ?? 0.4));
      const t = ctx?.currentTime ?? 0;
      tone(80 + i * 30, 0.32, 0.04 + i * 0.06, 'sine', t);
      tone(120 + i * 40, 0.32, 0.02 + i * 0.04, 'triangle', t + 0.02);
    },
    // Relay Forge (relay-forge.md §15)
    /** Soft wood-block metronome tick. accented=true → louder + higher (on-beat). */
    forgeMetroTick(accented?: boolean): void {
      const t = ctx?.currentTime ?? 0;
      const f = accented ? 1100 : 700;
      const v = accented ? 0.09 : 0.04;
      tone(f, 0.025, v, 'square', t);
      tone(f * 0.5, 0.02, v * 0.5, 'sine', t);
    },
    /** Metallic two-pitch clang. ±10% pitch variance per spec §15.3 to avoid mush. */
    hammerClang(pitch?: number): void {
      const p = pitch ?? (0.95 + Math.random() * 0.1);
      const t = ctx?.currentTime ?? 0;
      tone(1320 * p, 0.05, 0.14, 'square', t);
      tone(880 * p, 0.04, 0.12, 'square', t + 0.04);
      vibrate(15);
    },
    /** Brief swoosh — handoff between stages. */
    handoffSwoosh(): void {
      noise(0.1, 0.1);
      tone(880, 0.05, 0.08, 'sine');
    },
    /** Steam release — quench tap. ±10% pitch variance per spec §15.3. */
    steamHiss(pitch?: number): void {
      const p = pitch ?? (0.95 + Math.random() * 0.1);
      noise(0.18, 0.07);
      tone(2400 * p, 0.06, 0.04, 'triangle');
    },
    /** Single tick of the held-bellows rumble. Call repeatedly while held. */
    forgeHeat(intensity?: number): void {
      const i = Math.max(0, Math.min(1, intensity ?? 0.4));
      const t = ctx?.currentTime ?? 0;
      tone(70 + i * 25, 0.32, 0.05 + i * 0.05, 'sine', t);
      tone(110 + i * 35, 0.32, 0.025 + i * 0.04, 'triangle', t + 0.02);
    },
    /** Single tick of the polish-buff whir. Call repeatedly while dragging. */
    polishBuff(intensity?: number): void {
      const i = Math.max(0, Math.min(1, intensity ?? 0.4));
      const t = ctx?.currentTime ?? 0;
      tone(420 + i * 80, 0.18, 0.04 + i * 0.04, 'sawtooth', t);
      noise(0.08, 0.02 + i * 0.02);
    },
    /** Cha-ching on blade sale. Pitch +20% if perfect (spec §15.1). */
    bladeSell(perfect?: boolean): void {
      const p = perfect ? 1.2 : 1.0;
      const t = ctx?.currentTime ?? 0;
      tone(880 * p, 0.06, 0.16, 'triangle', t);
      tone(1320 * p, 0.08, 0.14, 'triangle', t + 0.05);
      tone(1760 * p, 0.18, 0.16, 'triangle', t + 0.12);
      vibrate(perfect ? [12, 30, 14] : 14);
    },
  },
  prefs: {
    get(): Prefs { return { ...prefs }; },
    setMusicVol(v: number): void {
      prefs.musicVol = Math.max(0, Math.min(1, v));
      if (musicGain && ctx) musicGain.gain.setValueAtTime(prefs.musicVol, ctx.currentTime);
      savePrefs(prefs);
    },
    setSfxVol(v: number): void {
      prefs.sfxVol = Math.max(0, Math.min(1, v));
      if (sfxGain && ctx) sfxGain.gain.setValueAtTime(prefs.sfxVol, ctx.currentTime);
      savePrefs(prefs);
    },
    setAmbienceVol(v: number): void {
      prefs.ambienceVol = Math.max(0, Math.min(1, v));
      if (ambientGain && ctx) ambientGain.gain.setValueAtTime(prefs.ambienceVol, ctx.currentTime);
      savePrefs(prefs);
    },
    setMuted(b: boolean): void {
      prefs.muted = b;
      if (masterGain && ctx) masterGain.gain.setValueAtTime(b ? 0 : 1, ctx.currentTime);
      savePrefs(prefs);
    },
    setHaptics(b: boolean): void {
      prefs.haptics = b;
      savePrefs(prefs);
    },
    toggleMute(): void { audio.prefs.setMuted(!prefs.muted); },
  },
};

// Backwards-compat shim for old @tt/feedback imports.
export const feedback = {
  tick: () => audio.sfx.tap(),
  success: () => audio.sfx.success(),
  fail: () => audio.sfx.fail(),
  cheer: () => audio.sfx.cheer(),
  bell: () => audio.sfx.bell(),
  countdownTick: () => audio.sfx.countdownTick(),
  countdownGo: () => audio.sfx.countdownGo(),
};
